import { Stack, SxProps, Theme } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";

import { MenuList } from "./menu-list";

type AnchorNavChildrenParams = {
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  setSectionRef: (el: HTMLDivElement | null, id: string) => void;
};

type AnchorNavProps = {
  menuPosition?: "left" | "right";
  anchorMenuItems: { id: string; label: string }[];
  anchorMenuTitle?: string;
  anchorMenuWidth?: number;
  initialActiveId?: string;
  children: (params: AnchorNavChildrenParams) => React.ReactNode;
  AnchorNavSxProps?: SxProps<Theme>;
  AnchorMenuListSxProps?: SxProps<Theme>;
  AnchorMenuListBoxSxProps?: SxProps<Theme>;
};

export const AnchorNav: React.FC<AnchorNavProps> = ({
  menuPosition = "left",
  anchorMenuItems,
  anchorMenuTitle,
  anchorMenuWidth,
  initialActiveId,
  children,
  AnchorNavSxProps,
  AnchorMenuListSxProps,
  AnchorMenuListBoxSxProps,
}) => {
  const [activeMenuItem, setActiveMenuItem] = useState<string>(
    initialActiveId ?? anchorMenuItems[0]?.id ?? "",
  );

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const isScrollingProgrammatically = useRef(false);
  const anchorMenuItemsRef = useRef(anchorMenuItems);
  anchorMenuItemsRef.current = anchorMenuItems;
  const hasScrolledToInitial = useRef(false);

  useEffect(() => {
    const hasActiveMenuItem = anchorMenuItems.some(
      (menuItem) => menuItem.id === activeMenuItem,
    );
    if (!hasActiveMenuItem) {
      setActiveMenuItem(anchorMenuItems[0]?.id ?? "");
    }
  }, [activeMenuItem, anchorMenuItems]);

  useEffect(() => {
    if (!initialActiveId || hasScrolledToInitial.current) return;
    const section = sectionRefs.current[initialActiveId];
    if (section) {
      hasScrolledToInitial.current = true;
      isScrollingProgrammatically.current = true;
      setActiveMenuItem(initialActiveId);
      requestAnimationFrame(() => {
        section.scrollIntoView({ behavior: "smooth", block: "start" });
        setTimeout(() => {
          isScrollingProgrammatically.current = false;
        }, 500);
      });
    }
  }, [initialActiveId]);

  const setSectionRef = (el: HTMLDivElement | null, id: string): void => {
    sectionRefs.current[id] = el;
  };

  const handleScroll = useCallback(() => {
    if (isScrollingProgrammatically.current || !scrollContainerRef.current)
      return;

    const items = anchorMenuItemsRef.current;
    const container = scrollContainerRef.current;
    const containerTop = container.getBoundingClientRect().top;

    let closestId = items[0]?.id ?? "";
    let closestDistance = Infinity;

    for (const item of items) {
      const section = sectionRefs.current[item.id];
      if (section) {
        const distance = Math.abs(
          section.getBoundingClientRect().top - containerTop,
        );
        if (distance < closestDistance) {
          closestDistance = distance;
          closestId = item.id;
        }
      }
    }

    setActiveMenuItem(closestId);
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return (): void => container.removeEventListener("scroll", handleScroll);
    }
    return undefined;
  }, [handleScroll]);

  const handleAnchorClick = (menuId: string): void => {
    const section = sectionRefs.current[menuId];
    if (section && scrollContainerRef.current) {
      isScrollingProgrammatically.current = true;
      setActiveMenuItem(menuId);
      section.scrollIntoView({ behavior: "smooth", block: "start" });
      setTimeout(() => {
        isScrollingProgrammatically.current = false;
      }, 500);
    }
  };

  const menuList = (
    <MenuList
      menuItems={anchorMenuItems}
      activeMenuItem={activeMenuItem}
      menuTitle={anchorMenuTitle}
      menuWidth={anchorMenuWidth}
      onItemClick={handleAnchorClick}
      MenuListSxProps={AnchorMenuListSxProps}
      MenuListBoxSxProps={AnchorMenuListBoxSxProps}
    />
  );

  return (
    <Stack
      direction="row"
      spacing={0}
      sx={{ flex: 1, minHeight: 0, gap: 2, ...AnchorNavSxProps }}
    >
      {menuPosition === "left" && menuList}

      {children({ scrollContainerRef, setSectionRef })}

      {menuPosition === "right" && menuList}
    </Stack>
  );
};
